import React from 'react';
import moment from 'moment';
import {
  getNearestStartTime,
  getPositionFromTime,
  getScaleUnitCount,
  getTimeFromPosition,
} from 'components/timeline/utils';
import { TimelineContextValue, TimelineProviderProps } from './types';

/**
 * Place the TimelineProvider at the root of the view nearest where you will use the timeline yet want
 * state related to the timeline to be shared.
 */
export function useTimelineContextManager(
  props: TimelineProviderProps,
): TimelineContextValue {
  const {
    currentTime,
    initialScale = 'hours',
    initialTime = Number(moment().format('x')),
    onCurrentTimeChange,
  } = props;

  // The current time in the timeline, represented as a timestamp
  const [currentTimeStamp, setCurrentTimeStamp] = React.useState(
    currentTime ? Number(currentTime.format('x')) : initialTime,
  );

  // The current scale of the timeline, e.g., 'hours', 'days', 'weeks'
  const [scale, setScale] = React.useState(initialScale);

  const timelineUnitCount = getScaleUnitCount(scale);

  // The timeline's current start time
  const [startTime, setStartTime] = React.useState(
    getNearestStartTime(initialTime, scale),
  );

  // The current time in the timeline, represented as a timestamp
  const [markerPosition, setMarkerPosition] = React.useState(
    getPositionFromTime(initialTime, startTime, scale, timelineUnitCount),
  );

  // Track if a video within the context is moving the current time along
  const [isPlaying, setIsPlaying] = React.useState(false);

  function changeMarkerPosition(newPosition: number): void {
    setMarkerPosition(newPosition);
    const newTimeStamp = getTimeFromPosition(
      newPosition,
      startTime,
      scale,
      timelineUnitCount,
    );
    setCurrentTimeStamp(newTimeStamp);
    onCurrentTimeChange?.(moment(newTimeStamp));
  }

  function changeCurrentTime(newTimeStamp: number): void {
    setCurrentTimeStamp(newTimeStamp);
    setMarkerPosition(
      getPositionFromTime(newTimeStamp, startTime, scale, timelineUnitCount),
    );
    onCurrentTimeChange?.(moment(newTimeStamp));
  }

  function changeScale(newScale: TimelineContextValue['scale']): void {
    const newUnitCount = getScaleUnitCount(newScale);
    const newStartTime = getNearestStartTime(currentTimeStamp, newScale);
    setScale(newScale);
    setStartTime(newStartTime);
    setMarkerPosition(
      getPositionFromTime(
        currentTimeStamp,
        newStartTime,
        newScale,
        newUnitCount,
      ),
    );
  }

  // Shorthand for getPositionFromTime that makes use of the relevant state values and simplifies use in views
  const getTimelinePositionForTime = React.useCallback(
    (time: number): number => {
      return getPositionFromTime(time, startTime, scale, timelineUnitCount);
    },
    [startTime, scale, timelineUnitCount],
  );

  const getTimelinePositionFromUnitIncrement = React.useCallback(
    (unitPosition: number): number => {
      return unitPosition / timelineUnitCount;
    },
    [timelineUnitCount],
  );

  React.useEffect(() => {
    if (currentTime) {
      const newTimeStamp = Number(currentTime.format('x'));
      setCurrentTimeStamp(newTimeStamp);
      const newStartTime = getNearestStartTime(newTimeStamp, scale);
      setStartTime(newStartTime);
      setMarkerPosition(
        getPositionFromTime(
          newTimeStamp,
          newStartTime,
          scale,
          timelineUnitCount,
        ),
      );
    }
  }, [currentTime?.toISOString()]);

  return React.useMemo(
    (): TimelineContextValue => ({
      changeCurrentTime,
      changeMarkerPosition,
      changeScale,
      currentTimeStamp,
      getTimelinePositionFromUnitIncrement,
      getTimelinePositionForTime,
      isPlaying,
      markerPosition,
      scale,
      setIsPlaying,
      startTime,
      timelineUnitCount,
    }),
    [currentTimeStamp, isPlaying, markerPosition, scale, timelineUnitCount],
  );
}
